import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart3, FileSpreadsheet } from "lucide-react";
import { downloadFile, inr, num } from "@/lib/providers";

function Stat({ label, value, sub }) {
  return (
    <Card className="p-4 border-slate-200 shadow-none">
      <p className="overline text-slate-500">{label}</p>
      <p className="text-2xl font-semibold text-navy mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
}

export default function ProjectDashboardTab() {
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [pid, setPid] = useState("");
  useEffect(() => { if (!pid && projects?.length) setPid(projects[0].id); }, [projects, pid]);
  const { data: d, isLoading } = useQuery({
    queryKey: ["projectDashboard", pid], enabled: !!pid,
    queryFn: async () => (await api.get(`/projects/${pid}/dashboard`)).data,
  });

  if (projects && !projects.length) return <EmptyState icon={BarChart3} title="No projects yet" hint="Create a project to see its progress here." />;
  const fields = d?.project?.log_fields || [];
  const maxDay = Math.max(1, ...(d?.daily || []).map((x) => x.verified));

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div className="w-72">
          <Label>Project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Choose project" /></SelectTrigger>
            <SelectContent className="bg-white">{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.city}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {pid && <Button variant="outline" onClick={() => downloadFile(`/projects/${pid}/export`, `${d?.project?.code || "project"}_field_logs.xlsx`)}><FileSpreadsheet className="h-4 w-4 mr-2" /> Export field logs (Excel)</Button>}
      </div>
      {!pid || isLoading || !d ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Stat label={`${d.project.unit_label} (verified)`} value={num(d.totals.verified)} sub={`of ${num(d.totals.target)} target · ${d.totals.percent}%`} />
            <Stat label="Contract value" value={inr(d.totals.contract_value)} sub={`${d.per_provider.length} work order(s)`} />
            <Stat label="Logs to verify" value={num(d.totals.pending_logs)} sub={d.totals.flagged_outside_city ? `${d.totals.flagged_outside_city} flagged outside city` : "None flagged"} />
            {fields.slice(0, 1).map((f) => <Stat key={f.key} label={f.label} value={num(d.totals.extra[f.key])} />)}
          </div>
          {fields.length > 1 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {fields.slice(1).map((f) => <Stat key={f.key} label={f.label} value={num(d.totals.extra[f.key])} />)}
            </div>
          )}

          {d.daily.length > 0 && (
            <Card className="p-4 border-slate-200 shadow-none mb-4">
              <p className="overline text-slate-500 mb-3">Verified {d.project.unit_label.toLowerCase()} per day</p>
              <div className="flex items-end gap-1 h-32 overflow-x-auto">
                {d.daily.map((x) => (
                  <div key={x.date} className="flex flex-col items-center justify-end h-full min-w-[22px]" title={`${x.date}: ${num(x.verified)}`}>
                    <div className="w-4 bg-teal rounded-t" style={{ height: `${(x.verified / maxDay) * 100}%` }} />
                    <span className="text-[9px] text-slate-400 mt-1">{x.date.slice(8)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="border-slate-200 shadow-none overflow-x-auto">
              <p className="overline text-slate-500 px-4 pt-3">By provider</p>
              <Table>
                <TableHeader><TableRow>
                  {["Provider", "Progress", "Billed"].map((h) => <TableHead key={h} className="overline text-slate-500">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {d.per_provider.map((p) => {
                    const pct = Math.min(100, (100 * p.verified) / p.target);
                    return (
                      <TableRow key={p.work_order_id}>
                        <TableCell className="text-navy font-medium">{p.provider_name}<div className="text-xs text-slate-400">{p.provider_code} · {p.wo_number}</div></TableCell>
                        <TableCell className="min-w-[160px]">
                          <div className="h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-teal" style={{ width: `${pct}%` }} /></div>
                          <div className="text-xs text-slate-500 mt-1">{num(p.verified)} / {num(p.target)}{p.pending_logs ? ` · ${p.pending_logs} to verify` : ""}</div>
                        </TableCell>
                        <TableCell className="text-sm">{inr(p.billed_value)}<div className="text-xs text-slate-400">of {inr(p.contract_value)}</div></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
            <Card className="border-slate-200 shadow-none overflow-x-auto">
              <p className="overline text-slate-500 px-4 pt-3">By area</p>
              <Table>
                <TableHeader><TableRow>
                  {["Area", "Verified", ...fields.map((f) => f.label)].map((h) => <TableHead key={h} className="overline text-slate-500">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {d.per_area.length === 0 && <TableRow><TableCell colSpan={2 + fields.length} className="text-slate-400 text-sm">No verified logs yet.</TableCell></TableRow>}
                  {d.per_area.map((a) => (
                    <TableRow key={a.area}>
                      <TableCell className="text-sm">{a.area}<div className="text-xs text-slate-400">{a.logs} log(s)</div></TableCell>
                      <TableCell className="text-sm font-semibold">{num(a.verified)}</TableCell>
                      {fields.map((f) => <TableCell key={f.key} className="text-sm">{num(a.extra[f.key])}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
